import { Component, OnInit, inject, EnvironmentInjector, runInInjectionContext } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { OrganizationService } from '../../core/services/organization.service';
import { AnalyticsService } from '../../core/services/analytics.service';
import { AuthenticationService } from '../../core/services/auth.service';
import { Auth, authState } from '@angular/fire/auth';
import { Firestore, doc, getDoc } from '@angular/fire/firestore';
import { firstValueFrom } from 'rxjs';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-organization-setup',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './organization-setup.component.html',
  styleUrls: ['./organization-setup.component.scss']
})
export class OrganizationSetupComponent implements OnInit {

  organizationId: string = '';
  organizationName: string = '';
  currentUser: any = null;
  isLoading: boolean = false;

  private auth = inject(Auth);;
  private authState$ = authState(this.auth);
  private analytics = inject(AnalyticsService);
  private firestore = inject(Firestore);
  private injector = inject(EnvironmentInjector);

  constructor(
    private organizationService: OrganizationService,
    private authenticationService: AuthenticationService,
    private router: Router
  ) { }

  async ngOnInit(): Promise<void> {
    this.currentUser = await firstValueFrom(this.authState$);
    if (this.currentUser) {
      console.log('User from AngularFireAuth:', this.currentUser);

      // Check if user is an owner - only owners can set up organizations
      const userDocRef = doc(this.firestore, 'users', this.currentUser.uid);
      const userDocSnap = await runInInjectionContext(this.injector, () => getDoc(userDocRef));

      if (userDocSnap.exists()) {
        const userRole = userDocSnap.data()['role'];

        if (userRole !== 'owner') {
          console.warn('User is not an owner - cannot set up organization');
          await Swal.fire({
            icon: 'info',
            title: 'Access Required',
            html: 'Only organization owners can set up organizations.<br><br>Please ask your organization owner to invite you, or contact support if you believe this is an error.',
            confirmButtonText: 'Go to Login'
          });
          this.router.navigate(['/auth/login']);
          return;
        }
      }

      const idTokenResult = await this.currentUser.getIdTokenResult();
      if (idTokenResult.claims && idTokenResult.claims.firebase) {
        const signInAttrs = idTokenResult.claims.firebase.sign_in_attributes;
        if (signInAttrs) {
          this.organizationId = signInAttrs.organization_id?.toString();
          this.organizationName = signInAttrs.organization_name || 'Your Organization';
        }
      }

      console.log('Organization ID:', this.organizationId);
      console.log('Organization Name:', this.organizationName);

      // If no org info in OIDC claims, show error
      if (!this.organizationId) {
        console.warn('No organization information found in user profile');
      }
    } else {
      console.error('No stored user found');
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No user information found. Please log in again.'
      });
    }
  }

  async setupOrganization() {
    if (!this.organizationId || !this.currentUser) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Missing organization or user information'
      });
      return;
    }

    this.isLoading = true;

    try {
      console.log('Waiting for Firebase Auth state...');

      // Get the current auth state using the observable (properly)
      const firebaseUser = await firstValueFrom(this.authState$);

      if (!firebaseUser) {
        this.isLoading = false;
        console.error('No authenticated Firebase user found');
        Swal.fire({
          icon: 'error',
          title: 'Authentication Required',
          text: 'Please log in again to continue.'
        });
        this.router.navigate(['/auth/login']);
        return;
      }

      console.log('✓ Firebase Auth ready. User:', firebaseUser.uid);

      // Force token refresh to ensure Firestore has latest auth state
      await firebaseUser.getIdToken(true);
      console.log('✓ Auth token refreshed');

      this.performSetup(firebaseUser.uid);

    } catch (error) {
      this.isLoading = false;
      console.error('Error during setup:', error);
      Swal.fire({
        icon: 'error',
        title: 'Setup Failed',
        text: 'An error occurred during setup. Please try logging in again.'
      });
      this.router.navigate(['/auth/login']);
    }
  }

  private async performSetup(userUid: string) {
    // Creation, the owner link, the encryption key and the custom claims all
    // happen in one server-side call now. This used to be three chained
    // client writes, where a failure between them left a half-created org.
    try {
      console.log('Fetching Planning Center organization details...');
      const pcoOrgDetails: any = await this.authenticationService.fetchPlanningCenterOrganization();
      console.log('✓ Planning Center organization details fetched:', pcoOrgDetails);

      const result = await this.organizationService.createOrganization(
        this.organizationName,
        {
          avatarUrl: pcoOrgDetails?.avatarUrl,
          contactWebsite: pcoOrgDetails?.contactWebsite,
          countryCode: pcoOrgDetails?.countryCode,
          dateFormat: pcoOrgDetails?.dateFormat,
          timezone: pcoOrgDetails?.timezone
        }
      );

      console.log('✓ Organization created:', result);

      this.analytics.logEvent('organization_management', {
        action: 'create_organization',
        organization_id: result.organizationId,
        organization_name: result.organizationName,
        has_pco_integration: result.orgType === 'pco'
      });

      // The callable set new custom claims; refresh the token so the guards
      // and Firestore rules see them on the very next navigation.
      await this.currentUser.getIdToken(true);

      this.isLoading = false;

      await Swal.fire({
        icon: 'success',
        title: 'Organization Created!',
        text: `Your organization "${result.organizationName}" has been set up successfully!`,
        confirmButtonText: 'Continue'
      });

      // Go to the first step this organization actually needs.
      const firstStep = result.requiredSteps?.[0] ?? 'webhook-setup';
      this.router.navigate([`/${firstStep}`]);
    } catch (error) {
      this.isLoading = false;
      console.error('Error creating organization:', error);
      Swal.fire({
        icon: 'error',
        title: 'Setup Failed',
        text: (error as { message?: string })?.message ||
          'Failed to create your organization. Please try again.'
      });
    }
  }
}